"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  applyFindings,
  checkHyphensAndDashes,
  DEFAULT_OPTIONS,
  type Finding,
  type RuleId,
  type Style,
} from "@/lib/tool-kit/style-checkers/hyphen-dash";

/** English strings. A translated content file overrides these through `ui`. */
export const HYPHEN_AND_DASH_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, a reference list or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "Participants aged 18-25 completed the survey from 2010-2015 -- a well known low income sample. The highly-educated group was a 5 year old cohort, and the effects were long-term. See pp. 45-67 and the Mann-Whitney U test.\n\nOpen Monday-Friday. The sample was very well-known.",
  styleLabel: "Style guide",
  styleApa: "APA 7",
  styleChicago: "Chicago 18",
  allowSpacedEn: "Allow spaced en dashes as British-style asides ( – )",
  skipQuotes: "Skip quoted passages",
  enterPrompt:
    "Paste or type some text to check its hyphens, en dashes and em dashes.",
  results: "Results",
  total: "Things to look at",
  fixable: "Ready to fix",
  needsLook: "Need a look",
  rulesHit: "Kinds of issue",
  readingNone:
    "No hyphen or dash problems found among the patterns this checker knows. It covers ranges, dashes used as punctuation and a list of common compounds — not every hyphen in the language.",
  readingSome:
    "Ready-to-fix items are mechanical: the replacement is shown and one click applies it. Items that need a look depend on your sentence.",
  applyAll: "Apply all {count} fixes",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with hyphen and dash issues highlighted",
  legendFix: "Ready to fix",
  legendCheck: "Needs a look",
  apply: "Apply",
  joinWord: "(join the word)",
  moreHidden: "Showing the first {count}.",
  sourceLabel: "Source:",
  ruleTitle: {
    "range-hyphen": "Hyphen in a range",
    "range-em": "Em dash in a range",
    "range-spaced": "Spaces around a range dash",
    "range-words": "Hyphen in a day or month range",
    "range-from": "En dash after “from” or “between”",
    "dash-double": "Double hyphen used as a dash",
    "dash-spaced-hyphen": "Spaced hyphen used as a dash",
    "dash-spaced-em": "Spaces around an em dash",
    "dash-spaced-en": "Spaced en dash",
    "en-between-words": "En dash between two lowercase words",
    "compound-open": "Compound modifier without a hyphen",
    "compound-after": "Hyphen in a compound after its noun",
    "ly-hyphen": "Hyphen after an -ly adverb",
    "number-unit": "Number and unit before a noun",
    "name-pair": "Hyphen between two people’s names",
    "line-break": "Hyphen left from a line break",
  },
  ruleWhy: {
    "range-hyphen":
      "A range of numbers takes an en dash (–), not a hyphen: pp. 45–67.",
    "range-em":
      "An em dash (—) sets off an aside. A range takes the shorter en dash.",
    "range-spaced": "A range dash is closed up: 45–67, not 45 – 67.",
    "range-words": "A range of days or months takes an en dash: Monday–Friday.",
    "range-from":
      "After “from” write “to”, and after “between” write “and”, rather than a dash.",
    "dash-double":
      "Two hyphens stand in for an em dash. Use the real character, closed up.",
    "dash-spaced-hyphen":
      "A hyphen with a space either side is not a dash. Use an em dash with no spaces.",
    "dash-spaced-em": "Both APA and Chicago set the em dash with no spaces.",
    "dash-spaced-en":
      "A spaced en dash is British practice for an aside. APA and Chicago use a closed em dash. Tick the option above to allow it.",
    "en-between-words":
      "An en dash between two plain words is usually a mistyped hyphen. It is correct only for equal-weight pairs (blue–green algae) or an open compound used as a modifier.",
    "compound-open":
      "Two words that modify a noun together take a hyphen before it: a well-known author.",
    "compound-after":
      "The same compound after the noun is left open: the author is well known.",
    "ly-hyphen":
      "An adverb ending in -ly is not hyphenated to the word it modifies: highly educated.",
    "number-unit":
      "A number and a unit that modify a noun are hyphenated: a 5-year-old child.",
    "name-pair":
      "Chicago 18 sets the names of two or more people, used as a modifier, with an en dash: Mann–Whitney test.",
    "line-break":
      "A hyphen followed by a line break is often left over from a PDF or hard-wrapped text. Check, then join the word.",
  },
  sourceApa: {
    ranges: "Publication Manual of the APA, 7th ed., Chapter 6 (dashes)",
    dashes: "Publication Manual of the APA, 7th ed., Chapter 6 (dashes)",
    compounds:
      "Publication Manual of the APA, 7th ed., §§ 6.11–6.12 and their tables",
    none: "",
  },
  sourceChicago: {
    ranges: "Chicago Manual of Style, 18th ed., ¶ 6.83",
    dashes: "Chicago Manual of Style, 18th ed., ¶¶ 6.79–6.100",
    compounds: "Chicago Manual of Style, 18th ed., ¶ 7.96",
    after: "Chicago Manual of Style, 18th ed., ¶ 7.92",
    names: "Chicago Manual of Style, 18th ed., ¶ 6.85",
    none: "",
  },
  limits:
    "The checker matches patterns and a list of common compounds. It does not read grammar, so a hyphen it does not know about is not reported, and a flagged phrase can be right in your sentence. When Merriam-Webster or your style guide says otherwise, follow that.",
} as const;

type Strings = typeof HYPHEN_AND_DASH_CHECKER_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const TONES = { fix: "rose", check: "amber" } as const;
const PER_RULE = 40;
const CONTEXT = 24;

type SourceKey = "ranges" | "dashes" | "compounds" | "after" | "names" | "none";

const SOURCE_BY_RULE: Record<RuleId, SourceKey> = {
  "range-hyphen": "ranges",
  "range-em": "ranges",
  "range-spaced": "ranges",
  "range-words": "ranges",
  "range-from": "dashes",
  "dash-double": "dashes",
  "dash-spaced-hyphen": "dashes",
  "dash-spaced-em": "dashes",
  "dash-spaced-en": "dashes",
  "en-between-words": "dashes",
  "compound-open": "compounds",
  "compound-after": "after",
  "ly-hyphen": "compounds",
  "number-unit": "compounds",
  "name-pair": "names",
  "line-break": "none",
};

function source(rule: RuleId, style: Style, strings: Strings): string {
  const key = SOURCE_BY_RULE[rule];
  if (style === "apa") {
    const table = strings.sourceApa;
    if (key === "after") {
      return table.compounds;
    }
    return key === "names" ? table.none : table[key];
  }
  return strings.sourceChicago[key];
}

const RE_WHITESPACE = /\s+/g;

function excerpt(text: string, finding: Finding): [string, string] {
  const before = text.slice(
    Math.max(0, finding.start - CONTEXT),
    finding.start
  );
  const after = text.slice(finding.end, finding.end + CONTEXT);
  return [
    before.replace(RE_WHITESPACE, " "),
    after.replace(RE_WHITESPACE, " "),
  ];
}

function FindingRow({
  finding,
  text,
  strings,
  onApply,
}: {
  finding: Finding;
  text: string;
  strings: Strings;
  onApply: (finding: Finding) => void;
}) {
  const [before, after] = excerpt(text, finding);
  const shown = finding.text.replace(RE_WHITESPACE, " ");
  const replacement =
    finding.replacement === "" ? strings.joinWord : finding.replacement;
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground">…{before}</span>
      <mark className={HIGHLIGHT_TONES.rose}>{shown}</mark>
      <span className="text-muted-foreground">{after}…</span>
      {replacement === null ? null : (
        <>
          <span aria-hidden="true" className="text-muted-foreground">
            →
          </span>
          <code className="rounded bg-muted px-2 py-0.5 text-foreground">
            {replacement}
          </code>
          <button
            className={`${BUTTON_SMALL} ms-auto`}
            onClick={() => onApply(finding)}
            type="button"
          >
            {strings.apply}
          </button>
        </>
      )}
    </li>
  );
}

function RuleGroup({
  rule,
  findings,
  text,
  style,
  strings,
  fmt,
  onApply,
}: {
  rule: RuleId;
  findings: readonly Finding[];
  text: string;
  style: Style;
  strings: Strings;
  fmt: Fmt;
  onApply: (finding: Finding) => void;
}) {
  const cite = source(rule, style, strings);
  return (
    <section className={`${CARD} space-y-3`}>
      <div className="space-y-1">
        <h4 className="font-medium text-foreground text-sm">
          {strings.ruleTitle[rule]}{" "}
          <span className="font-normal text-muted-foreground">
            ({fmt(findings.length)})
          </span>
        </h4>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.ruleWhy[rule]}
        </p>
        {cite ? (
          <p className={HINT}>
            {strings.sourceLabel} {cite}
          </p>
        ) : null}
      </div>
      <ul className="space-y-2">
        {findings.slice(0, PER_RULE).map((finding) => (
          <FindingRow
            finding={finding}
            key={finding.start}
            onApply={onApply}
            strings={strings}
            text={text}
          />
        ))}
      </ul>
      {findings.length > PER_RULE ? (
        <p className={HINT}>
          {t(strings.moreHidden, { count: fmt(PER_RULE) })}
        </p>
      ) : null}
    </section>
  );
}

export default function HyphenAndDashCheckerWidget() {
  const strings = useToolUi(HYPHEN_AND_DASH_CHECKER_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [style, setStyle] = useState<Style>("apa");
  const [allowSpacedEn, setAllowSpacedEn] = useState(false);
  const [skipQuotes, setSkipQuotes] = useState(true);
  const deferred = useDeferredValue(text);
  const report = useMemo(
    () =>
      checkHyphensAndDashes(deferred, {
        ...DEFAULT_OPTIONS,
        style,
        allowSpacedEn,
        skipQuotes,
      }),
    [deferred, style, allowSpacedEn, skipQuotes]
  );
  const hasText = deferred.trim().length > 0;
  const grouped = useMemo(() => {
    const groups = new Map<RuleId, Finding[]>();
    for (const finding of report.findings) {
      groups.set(finding.rule, [...(groups.get(finding.rule) ?? []), finding]);
    }
    return [...groups.entries()];
  }, [report.findings]);
  const fixes = report.findings.filter((f) => f.severity === "fix");

  function apply(chosen: readonly Finding[]) {
    if (text === deferred) {
      setText(applyFindings(text, chosen));
    }
  }

  const highlights: TextMatch[] = report.findings.map((finding) => ({
    start: finding.start,
    end: finding.end,
    category: finding.severity,
    title: strings.ruleTitle[finding.rule],
  }));

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.privacy}
        id="hdc-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleText)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <PillGroup
        label={strings.styleLabel}
        name="hdc-style"
        onChange={setStyle}
        options={[
          { value: "apa", label: strings.styleApa },
          { value: "chicago", label: strings.styleChicago },
        ]}
        value={style}
      />
      <div className="space-y-2">
        <Checkbox
          checked={allowSpacedEn}
          label={strings.allowSpacedEn}
          onChange={setAllowSpacedEn}
        />
        <Checkbox
          checked={skipQuotes}
          label={strings.skipQuotes}
          onChange={setSkipQuotes}
        />
      </div>

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.total}
                value={fmt(report.findings.length)}
              />
              <StatCard label={strings.fixable} value={fmt(report.fixable)} />
              <StatCard
                label={strings.needsLook}
                value={fmt(report.findings.length - report.fixable)}
              />
              <StatCard label={strings.rulesHit} value={fmt(grouped.length)} />
            </StatGrid>
            <Notice>
              {report.findings.length === 0
                ? strings.readingNone
                : strings.readingSome}
            </Notice>
            {fixes.length > 0 ? (
              <button
                className={BUTTON_PRIMARY}
                onClick={() => apply(fixes)}
                type="button"
              >
                {t(strings.applyAll, { count: fmt(fixes.length) })}
              </button>
            ) : null}
          </div>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <p className="flex flex-wrap gap-3 text-sm">
              <span className={HIGHLIGHT_TONES.rose}>{strings.legendFix}</span>
              <span className={HIGHLIGHT_TONES.amber}>
                {strings.legendCheck}
              </span>
            </p>
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
          </div>

          <div className="space-y-4">
            {grouped.map(([rule, findings]) => (
              <RuleGroup
                findings={findings}
                fmt={fmt}
                key={rule}
                onApply={(finding) => apply([finding])}
                rule={rule}
                strings={strings}
                style={style}
                text={deferred}
              />
            ))}
          </div>
          <p className={HINT}>{strings.limits}</p>
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
